import { api } from '../api';

// ── Types ─────────────────────────────────────────────────────────────

export type MessageInfo = {
  id: string;
  channel_id: string;
  sender_id: string;
  content: string;
  content_type: string;
  reply_to: string | null;
  edited_at: string | null;
  deleted_at: string | null;
  created_at: string;
};

export type ChannelInfo = {
  id: string;
  org_id: string;
  type: 'project' | 'direct';
  name: string | null;
  project_id: string | null;
  created_at: string;
  updated_at: string;
};

export type ChannelWithMeta = {
  channel: ChannelInfo;
  last_read_at: string | null;
  muted: boolean;
  unread_count: number;
  members: string[] | null;
};

// ── API calls ─────────────────────────────────────────────────────────

export const fetchChannels = async (): Promise<{ channels: ChannelWithMeta[] }> =>
  api.get('api/channels').json();

export const createDMChannel = async (data: {
  user_id: string;
}): Promise<ChannelWithMeta> =>
  api.post('api/channels/direct', { json: data }).json();

export const fetchMessages = async (
  channelId: string,
  params?: { limit?: number; before?: string },
): Promise<{ messages: MessageInfo[]; has_more: boolean }> => {
  const searchParams: Record<string, string> = {};
  if (params?.limit) searchParams.limit = String(params.limit);
  if (params?.before) searchParams.before = params.before;
  return api
    .get(`api/channels/${channelId}/messages`, { searchParams })
    .json();
};

export const sendMessage = async (
  channelId: string,
  data: { content: string; content_type?: string; reply_to?: string },
): Promise<MessageInfo> =>
  api.post(`api/channels/${channelId}/messages`, { json: data }).json();

export const editMessage = async (
  channelId: string,
  messageId: string,
  data: { content: string },
): Promise<MessageInfo> =>
  api
    .put(`api/channels/${channelId}/messages/${messageId}`, { json: data })
    .json();

export const deleteMessage = async (
  channelId: string,
  messageId: string,
): Promise<{ ok: boolean }> =>
  api.delete(`api/channels/${channelId}/messages/${messageId}`).json();

export const markChannelRead = async (
  channelId: string,
): Promise<{ ok: boolean }> =>
  api.post(`api/channels/${channelId}/read`).json();

export const muteChannel = async (
  channelId: string,
  muted: boolean,
): Promise<{ ok: boolean; muted: boolean }> =>
  api.put(`api/channels/${channelId}/mute`, { json: { muted } }).json();
